import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/cn';
import { useCampaigns, useContextTree, useCreateCampaign, type Campaign } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/campaigns/')({
  component: CampaignsList,
});

/** Design hand-off §3. Tailwind can't build a class name from a variable, so the map is literal. */
const STATUS_CLASS: Record<Campaign['status'], string> = {
  draft: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-600 dark:text-zinc-300',
  active: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  paused: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  archived: 'border-zinc-400/30 bg-transparent text-zinc-400',
};

export function StatusBadge({ status }: { status: Campaign['status'] }): React.JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium capitalize',
        STATUS_CLASS[status]
      )}
    >
      {status}
    </span>
  );
}

const PRIORITY_LABEL: Record<Campaign['priority'], string> = {
  active: 'Active',
  passive: 'Passive',
  time_sensitive: 'Time-sensitive',
};

function CampaignsList(): React.JSX.Element {
  const { appId, environmentId } = useContextStore();
  const { data: campaigns, isLoading } = useCampaigns(appId);

  // The list hook is app-scoped; environment is the publish target, so a campaign from another
  // environment is not this page's business.
  const rows = campaigns?.filter((c) => c.environment_id === environmentId) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Campaigns</h1>
          <p className="text-sm text-muted-foreground">
            What runs, for whom, and when — scoped to the selected environment.
          </p>
        </div>
        <NewCampaignDialog />
      </div>

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No campaigns in this environment"
          description="A campaign points a segment at an experience and decides when it fires. Create one, link it, publish it."
          action={<NewCampaignDialog />}
        />
      ) : (
        <div className="rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead className="text-right">Bundle version</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell className="font-medium">
                    <Link
                      to="/campaigns/$id"
                      params={{ id: campaign.id }}
                      className="block hover:underline"
                    >
                      {campaign.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={campaign.status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">{PRIORITY_LABEL[campaign.priority]}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {campaign.published_bundle_version ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function NewCampaignDialog(): React.JSX.Element {
  const navigate = useNavigate();
  const { appId, environmentId } = useContextStore();
  const { data: orgs } = useContextTree();
  const create = useCreateCampaign(appId);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [envId, setEnvId] = useState(environmentId ?? '');

  const environments =
    orgs
      ?.flatMap((o) => o.workspaces)
      .flatMap((w) => w.apps)
      .find((a) => a.id === appId)?.environments ?? [];

  function submit(): void {
    create.mutate(
      { name: name.trim(), environment_id: envId },
      {
        onSuccess: (campaign) => {
          setOpen(false);
          setName('');
          void navigate({ to: '/campaigns/$id', params: { id: campaign.id } });
        },
        onError: (error: Error) => toast.error(error.message),
      }
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setEnvId(environmentId ?? '');
      }}
    >
      <DialogTrigger asChild>
        <Button>New campaign</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New campaign</DialogTitle>
          <DialogDescription>
            It starts as a draft. Link a segment and an experience on the next screen, then publish.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="campaign-name">Name</Label>
            <Input
              id="campaign-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Onboarding nudge"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="campaign-env">Environment</Label>
            <Select value={envId} onValueChange={setEnvId}>
              <SelectTrigger id="campaign-env">
                <SelectValue placeholder="Select an environment" />
              </SelectTrigger>
              <SelectContent>
                {environments.map((env) => (
                  <SelectItem key={env.id} value={env.id} className="capitalize">
                    {env.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || !envId || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
